import { useMemo } from "react";

import { useHistory } from "react-router-dom";

import { enrichRowWithReportInfo } from "@/components/reports/reportListClick";
import {
  ConsumerReportOrderStatusBadge,
  OrderedReportStatusBadge
} from "@/components/reports/Badges";
import Group from "@/tpo/Group";
import Stack from "@/tpo/Stack";
import Table from "@/tpo/Table";
import { useTestStatuses, getUserTestPaymentStatus } from "@/tpo/TestStatuses";
import { getStatusVariant } from "@/tpo/shop/constants";
import { useTheme } from "styled-components";

import Box from "./Box";
import CircleButton from "./CircleButton";
import { ExternalTextLink } from "./Links";
import StatusBadge from "./StatusBadge";
import { SpanText } from "./Text";

const renderers = {
  test: ({ name, orderId, created, href, userTest }) => {
    const externalSourceTest = userTest?.externalSourceTest;

    return (
      <Stack fontFamily="gilroyMedium">
        <Box fontFamily="inherit" lineHeight="24px">
          {name}
        </Box>
        {(!!orderId || externalSourceTest || created) && (
          <Group fontFamily="inherit">
            {externalSourceTest ? (
              <SpanText fontFamily="inherit" lineHeight={1} color="#818181">
                External source
              </SpanText>
            ) : (
              !!orderId && (
                <ExternalTextLink
                  fontFamily="inherit"
                  lineHeight={1}
                  color="#818181"
                  newTab
                  href={href}
                >
                  {orderId}
                </ExternalTextLink>
              )
            )}
            {(!!orderId || externalSourceTest) && created && (
              <SpanText fontFamily="inherit" lineHeight={1} color="#818181" mx={1}>
                |
              </SpanText>
            )}
            {created && (
              <SpanText fontFamily="inherit" lineHeight={1} color="#818181">
                {created}
              </SpanText>
            )}
          </Group>
        )}
      </Stack>
    );
  },
  payment: ({ status }) => {
    const paymentVariant = status ? getStatusVariant(status) : undefined;
    return (
      paymentVariant && (
        <StatusBadge variant={paymentVariant} data-testid="paymentStatusBadge">
          {status}
        </StatusBadge>
      )
    );
  },
  status: ({ status, variant }) => {
    return (
      status && (
        <StatusBadge variant={variant} data-testid="progressStatusBadge">
          {status}
        </StatusBadge>
      )
    );
  },
  reportStatus: ({ reportViewType, hasUserReport }) => {
    if (reportViewType === "patient") {
      return <OrderedReportStatusBadge hasUserReport={hasUserReport} />;
    }
    return <ConsumerReportOrderStatusBadge isAvailable={hasUserReport} />;
  },
  action: ({ isUserTest, isLink, isReport, canClickReport }, { onTestClick, onReportClick }) => {
    if (isUserTest && isLink) {
      return (
        <CircleButton iconHeight={10} iconWidth={10} direction="right" onClick={onTestClick} />
      );
    }
    if (isReport && canClickReport) {
      return (
        <CircleButton iconHeight={10} iconWidth={10} direction="right" onClick={onReportClick} />
      );
    }
    return null;
  }
};

export default function OrdersAndTests({
  userTests,
  ordersInProgress,
  getOrderUrl,
  getTestResultUrl,
  getReportUrl,
  showPaymentStatus = true,
  reports = [],
  userReports = [],
  userEntitlements = [],
  reportViewType
}) {
  const rows = useTestStatuses({
    testResults: userTests,
    ordersInProgress
  });

  const history = useHistory();
  const theme = useTheme();

  const enrichedRows = useMemo(
    () =>
      rows.map(row =>
        enrichRowWithReportInfo(row, {
          reports,
          userReports,
          userEntitlements,
          reportViewType
        })
      ),
    [rows, reports, userReports, userEntitlements, reportViewType]
  );

  const columns = useMemo(() => {
    const cols = [
      {
        id: "test",
        label: "Test",
        accessor: row => {
          return renderers.test({
            ...row,
            href: getOrderUrl(row.orderId)
          });
        },
        width: "40%"
      },
      {
        id: "status",
        accessor: row => {
          if (row.isReport) {
            return renderers.reportStatus({
              reportViewType,
              hasUserReport: row.orderTestItem?.hasUserReport
            });
          }
          return renderers.status({
            status: row.status,
            variant: row.variant
          });
        },
        label: "Sample Status",
        width: "20%"
      },
      {
        id: "action",
        accessor: row => (
          <Group justifyContent="flex-end">
            {renderers.action(row, {
              onTestClick: () => {
                history.push(getTestResultUrl(row.userTest.id));
              },
              onReportClick: () => {
                if (row.report && getReportUrl) {
                  history.push(getReportUrl(row.report));
                }
              }
            })}
          </Group>
        ),
        label: "",
        align: "center"
      }
    ];

    if (showPaymentStatus) {
      cols.splice(1, 0, {
        id: "payment",
        accessor: ({ userTest, orderTestItem }) => {
          return renderers.payment({
            status: userTest ? getUserTestPaymentStatus(userTest) : orderTestItem.order.derivedPaymentStatus
          });
        },
        label: "Payment Status",
        width: "25%"
      });
    }

    return cols;
  }, [history, getOrderUrl, getTestResultUrl, getReportUrl, showPaymentStatus, reportViewType]);

  return (
    <>
      <Box display={["none", "none", "block"]}>
        {/* no pagination for now */}
        <Table columns={columns} loading={false} rows={enrichedRows} />
      </Box>
      <Stack display={["block", "block", "none"]} gap={0}>
        {enrichedRows.map(row => (
          <Stack key={row.id} gap={8} p={20} borderBottom={`1px solid ${theme.colors.haze}`}>
            {renderers.test({
              ...row,
              href: getOrderUrl(row.orderId)
            })}
            <Group alignItems="center" gap={8}>
              {showPaymentStatus &&
                renderers.payment({
                  status: row.userTest
                    ? getUserTestPaymentStatus(row.userTest)
                    : row.orderTestItem.order.derivedPaymentStatus
                })}
              {row.isReport
                ? renderers.reportStatus({
                    reportViewType,
                    hasUserReport: row.orderTestItem?.hasUserReport
                  })
                : renderers.status(row)}
            </Group>
            <Group justifyContent="flex-end">
              {renderers.action(
                {
                  ...row
                },
                {
                  onTestClick: () => {
                    history.push(getTestResultUrl(row.userTest.id));
                  },
                  onReportClick: () => {
                    if (row.report && getReportUrl) {
                      history.push(getReportUrl(row.report));
                    }
                  }
                }
              )}
            </Group>
          </Stack>
        ))}
      </Stack>
    </>
  );
}
